import { z } from 'zod';
import {
  ENROLLMENT_STATUSES,
  MAX_COURSE_SKILLS,
  MAX_RATING,
  NEED_SOURCES,
  NEED_STATUSES,
  SESSION_STATUSES,
  TRAINING_KINDS,
  TRAINING_TRACKS,
} from './learning.js';

/** مخططات التدريب والتطوير. */

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ بصيغة سنة-شهر-يوم.');

const optionalDateOnly = dateOnly
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

const money = z.coerce.number().min(0).max(99_999_999_999.99);

/* ==========================================================================
   الاحتياجات
   ========================================================================== */

/**
 * احتياجٌ لشخصٍ أو لوظيفة.
 *
 * ولا بدّ من أحدهما: احتياجٌ بلا صاحبٍ ولا مقعدٍ جملةٌ في الهواء لا تُدرَج
 * في خطّةٍ ولا تُغلق بحضور.
 */
export const UpsertNeedSchema = z
  .object({
    employeeId: Cuid.nullable().optional(),
    positionId: Cuid.nullable().optional(),
    title: z.string().trim().min(3, 'عنوان الاحتياج قصير.').max(200),
    description: optionalText(2000),
    source: z.enum(NEED_SOURCES).default('MANAGER'),
    priority: z.coerce
      .number()
      .int()
      .min(1, 'الأولويّة من واحدٍ إلى ثلاثة.')
      .max(3, 'الأولويّة من واحدٍ إلى ثلاثة.')
      .default(2),
    reviewId: Cuid.nullable().optional(),
    dueOn: optionalDateOnly,
    notes: optionalText(1000),
  })
  .refine((v) => Boolean(v.employeeId) || Boolean(v.positionId), {
    message: 'اكتبه لموظّفٍ أو لوصفٍ وظيفيّ — أحدُهما على الأقلّ.',
    path: ['employeeId'],
  });
export type UpsertNeedInput = z.infer<typeof UpsertNeedSchema>;

/**
 * إغلاقُ احتياج.
 *
 * الخلاصةُ مطلوبةٌ عند الإسقاط: احتياجٌ يختفي بلا سببٍ مكتوبٍ يعود من
 * يقرأ الملفَّ بعد سنةٍ فيكتبه من جديد.
 */
export const CloseNeedSchema = z.object({
  status: z.enum(NEED_STATUSES),
  outcome: optionalText(1000),
});
export type CloseNeedInput = z.infer<typeof CloseNeedSchema>;

export const NeedsQuerySchema = z.object({
  employeeId: Cuid.optional(),
  positionId: Cuid.optional(),
  status: z.enum(NEED_STATUSES).optional(),
  source: z.enum(NEED_SOURCES).optional(),
});
export type NeedsQuery = z.infer<typeof NeedsQuerySchema>;

/* ==========================================================================
   الدورات
   ========================================================================== */

const skills = z
  .array(z.string().trim().min(2, 'المهارة قصيرة.').max(80))
  .max(MAX_COURSE_SKILLS, `المهارات أكثر من ${MAX_COURSE_SKILLS}.`);

export const UpsertCourseSchema = z.object({
  code: optionalText(30),
  title: z.string().trim().min(3, 'اسم الدورة قصير.').max(160),
  kind: z.enum(TRAINING_KINDS).default('INTERNAL'),
  provider: optionalText(160),
  description: optionalText(4000),
  hours: z.coerce.number().min(0).max(9999).nullable().optional(),
  defaultCost: money.nullable().optional(),
  track: z.enum(TRAINING_TRACKS).default('GENERAL'),
  skills: skills.default([]),
  isActive: z.boolean().default(true),
});
export type UpsertCourseInput = z.infer<typeof UpsertCourseSchema>;

export const CoursesQuerySchema = z.object({
  track: z.enum(TRAINING_TRACKS).optional(),
  kind: z.enum(TRAINING_KINDS).optional(),
  q: z.string().trim().max(120).optional(),
  /** الموقوفةُ تُخفى افتراضًا — تُطلب صراحةً. */
  all: z.enum(['1']).optional(),
});
export type CoursesQuery = z.infer<typeof CoursesQuerySchema>;

/* ==========================================================================
   الانعقاد
   ========================================================================== */

export const UpsertSessionSchema = z
  .object({
    courseId: Cuid,
    startsOn: dateOnly,
    endsOn: dateOnly,
    location: optionalText(200),
    trainer: optionalText(160),
    capacity: z.coerce
      .number()
      .int('عدد المقاعد رقم صحيح.')
      .min(1, 'المقاعد واحدٌ فأعلى.')
      .max(9999)
      .nullable()
      .optional(),
    cost: money.nullable().optional(),
    notes: optionalText(2000),
  })
  .refine((v) => v.endsOn >= v.startsOn, {
    message: 'نهاية الانعقاد قبل بدايته.',
    path: ['endsOn'],
  });
export type UpsertSessionInput = z.infer<typeof UpsertSessionSchema>;

export const SessionStatusSchema = z.object({
  status: z.enum(SESSION_STATUSES),
});
export type SessionStatusInput = z.infer<typeof SessionStatusSchema>;

export const SessionsQuerySchema = z.object({
  courseId: Cuid.optional(),
  status: z.enum(SESSION_STATUSES).optional(),
});
export type SessionsQuery = z.infer<typeof SessionsQuerySchema>;

/* ==========================================================================
   القيد والنتيجة
   ========================================================================== */

/**
 * قيدُ مقاعدَ دفعةً واحدة.
 *
 * ومن قُيّد قبلُ يُتجاوَز بلا خطأ: قائمةُ الترشيح تُبنى بالاختيار من جدول،
 * ومن أُضيف مرّتين لا يجوز أن يُسقط الدفعةَ كلَّها.
 */
export const EnrollSchema = z.object({
  employeeIds: z
    .array(Cuid)
    .min(1, 'اختر موظّفًا واحدًا على الأقلّ.')
    .max(200, 'المقاعد المطلوبة أكثر من مئتين.'),
  needId: Cuid.nullable().optional(),
});
export type EnrollInput = z.infer<typeof EnrollSchema>;

export const EnrollmentResultSchema = z.object({
  status: z.enum(ENROLLMENT_STATUSES),
  score: z.coerce.number().min(0).max(100).nullable().optional(),
  passed: z.boolean().nullable().optional(),
  rating: z.coerce
    .number()
    .int('التقدير رقم صحيح.')
    .min(1, `التقدير بين ١ و${MAX_RATING}.`)
    .max(MAX_RATING, `التقدير بين ١ و${MAX_RATING}.`)
    .nullable()
    .optional(),
  feedback: optionalText(2000),
  certificateNo: optionalText(60),
});
export type EnrollmentResultInput = z.infer<typeof EnrollmentResultSchema>;

export const EnrollmentsQuerySchema = z.object({
  sessionId: Cuid.optional(),
  employeeId: Cuid.optional(),
  status: z.enum(ENROLLMENT_STATUSES).optional(),
});
export type EnrollmentsQuery = z.infer<typeof EnrollmentsQuerySchema>;
